import { useEffect, useRef, useState } from "react";
import { getOrgMembers } from "@/features/organizations/api";
import type { OrganizationMember } from "@/types";

export default function TransferOwnershipModal({
  orgId,
  currentOwnerId,
  onTransfer,
  onClose,
}: {
  orgId: string;
  currentOwnerId: string;
  onTransfer: (targetUserId: string) => void;
  onClose: () => void;
}) {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key === "Tab") {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  useEffect(() => {
    (async () => {
      const { data } = await getOrgMembers(orgId);
      if (data) {
        setMembers(data.filter((m) => m.userId !== currentOwnerId && m.role !== "owner"));
      }
      setLoading(false);
    })();
  }, [orgId, currentOwnerId]);

  function handleTransfer() {
    if (!selected) return;
    onTransfer(selected);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(0,0,0,0.4)]"
      role="dialog"
      aria-modal="true"
      aria-label="Transfer ownership"
      ref={dialogRef}
      onKeyDown={handleKeyDown}
    >
      <div className="bg-white rounded-[20px] p-5 w-[90%] max-w-md shadow-[1px_1px_0px_0px_#1c1b2e] border border-text flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <span className="text-text text-[1.125rem] font-black">Transfer Ownership</span>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full border border-text flex items-center justify-center bg-white cursor-pointer"
          >
            <span className="text-text font-bold">✕</span>
          </button>
        </div>

        <p className="text-text text-sm leading-5">
          Select a new owner for this organization. You will be demoted to admin.
        </p>

        {loading ? (
          <div className="text-text-muted text-sm text-center py-4">Loading members...</div>
        ) : members.length === 0 ? (
          <div className="text-text-muted text-sm text-center py-4">
            No eligible members to transfer ownership to.
          </div>
        ) : (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {members.map((member) => (
              <button
                key={member.userId}
                type="button"
                onClick={() => {
                  setSelected(member.userId);
                  setConfirming(true);
                }}
                className={`flex items-center gap-3 p-3 rounded-[12px] cursor-pointer border text-left font-inherit ${
                  selected === member.userId
                    ? "border-purple bg-[rgba(156,77,255,0.06)]"
                    : "border-[rgba(28,27,46,0.1)] bg-card"
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-[rgba(156,77,255,0.1)] flex items-center justify-center shrink-0">
                  <span className="text-purple text-sm font-extrabold">
                    {member.displayName?.charAt(0)?.toUpperCase() ?? "?"}
                  </span>
                </div>
                <div className="flex-1">
                  <span className="text-text text-sm font-bold block">{member.displayName}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {confirming && selected && (
          <div className="flex gap-2 pt-2 border-t border-[rgba(28,27,46,0.1)]">
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setConfirming(false);
              }}
              className="flex-1 bg-card rounded-[16px] py-3 text-text text-[0.875rem] font-extrabold border border-text cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleTransfer}
              className="flex-1 bg-purple rounded-[16px] py-3 text-white text-[0.875rem] font-extrabold border border-text cursor-pointer"
            >
              Transfer ownership
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
